import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { orderAPI, reviewAPI, paymentAPI, supportAPI } from '../../services/api';
import socketService from '../../services/socket';
import Navbar from '../../components/Navbar';
import LiveTracking from '../../components/LiveTracking';
import { useAuth } from '../../context/AuthContext';

function OrderTracking() {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth(); // Use AuthContext for authenticated user data
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [hasReview, setHasReview] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [review, setReview] = useState({
    restaurantRating: 5,
    restaurantReview: '',
    deliveryRating: 5,
    deliveryReview: ''
  });
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpCategory, setHelpCategory] = useState('');
  const [helpMessage, setHelpMessage] = useState('');
  const [helpSubmitting, setHelpSubmitting] = useState(false);
  const fallbackTimerRef = useRef(null);

  // Initialize socket connection and listeners
  useEffect(() => {
    // Connect to socket
    socketService.connect();

    // Listen for connection status
    const unsubConnection = socketService.on('connection', (connected) => {
      console.log('🔌 Socket connection status:', connected);
      setSocketConnected(connected);

      if (connected) {
        // Clear fallback polling when socket connects
        if (fallbackTimerRef.current) {
          clearInterval(fallbackTimerRef.current);
          fallbackTimerRef.current = null;
        }
      } else {
        // Start fallback polling when socket disconnects
        startFallbackPolling();
      }
    });

    // Listen for order updates (status changes)
    const unsubOrderUpdate = socketService.on('order_update', (data) => {
      console.log('📡 Order update received:', data);
      if (data.orderId === id) {
        // Refresh order data when we receive an update
        fetchOrder();
      }
    });

    // Listen for real-time delivery location updates
    const unsubDeliveryLocation = socketService.on('delivery_location', (data) => {
      console.log('📍 Delivery location received:', data);
      if (data.orderId === id) {
        setDeliveryLocation({
          lat: data.lat,
          lng: data.lng,
          timestamp: data.timestamp
        });
      }
    });

    // Listen for order cancellations
    const unsubOrderCancelled = socketService.on('order_cancelled', (data) => {
      console.log('❌ Order cancelled:', data);
      if (data.orderId === id) {
        // Refresh order to show cancelled status
        fetchOrder();
      }
    });

    // Join user room using authenticated user from AuthContext
    if (user?._id) {
      socketService.joinUserRoom(user._id);
    }

    // Join the specific order room for real-time tracking
    socketService.joinOrderRoom(id);

    return () => {
      // Cleanup socket listeners
      unsubConnection();
      unsubOrderUpdate();
      unsubDeliveryLocation();
      unsubOrderCancelled();
      // Leave order room
      socketService.leaveOrderRoom(id);
      // Clear fallback timer
      if (fallbackTimerRef.current) {
        clearInterval(fallbackTimerRef.current);
      }
    };
  }, [id, user]); // Add user as dependency to re-join room if user changes

  const startFallbackPolling = () => {
    if (!fallbackTimerRef.current) {
      console.log('📡 Starting fallback polling (socket disconnected)');
      fallbackTimerRef.current = setInterval(fetchOrder, 10000);
    }
  };

  useEffect(() => {
    fetchOrder();
    checkExistingReview();
  }, [id]);

  // Verify payment when returning from Cashfree payment gateway
  useEffect(() => {
    const verifyPaymentStatus = async () => {
      const searchParams = new URLSearchParams(location.search);
      const paymentStatus = searchParams.get('payment_status');

      // Only verify if payment_status is present and we haven't verified yet
      if (paymentStatus === 'success' && !paymentVerified) {
        console.log('🔍 Verifying payment for order:', id);
        try {
          const response = await paymentAPI.verifyPayment(id);
          console.log('✅ Payment verification response:', response.data);

          if (response.data.success) {
            setPaymentVerified(true);
            // Refresh order to get updated payment status
            fetchOrder();
          }
        } catch (error) {
          console.error('❌ Payment verification failed:', error);
        }
      }
    };

    verifyPaymentStatus();
  }, [location.search, id, paymentVerified]);

  const fetchOrder = async () => {
    try {
      const response = await orderAPI.getById(id);
      setOrder(response.data.data.order);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      setLoading(false);
    }
  };

  const checkExistingReview = async () => {
    try {
      const response = await reviewAPI.getByOrder(id);
      if (response.data.data.review) {
        setHasReview(true);
      }
    } catch (error) {
      // 404 means no review exists yet, which is expected and fine
      if (error.response?.status === 404) {
        setHasReview(false);
      } else {
        // Other errors (500, network issues, etc.)
        console.error('Error checking review:', error);
        setHasReview(false);
      }
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await reviewAPI.create({
        orderId: id,
        ...review
      });
      alert('Review submitted successfully!');
      setShowReview(false);
      setHasReview(true);
    } catch (error) {
      alert('Failed to submit review: ' + (error.response?.data?.message || error.message));
    }
  };

  const submitHelpRequest = async (e) => {
    e.preventDefault();
    if (!helpCategory || !helpMessage.trim()) {
      alert('Please select an issue category and describe your problem');
      return;
    }

    setHelpSubmitting(true);
    try {
      await supportAPI.createTicket({
        category: helpCategory,
        subject: `Order #${order.orderNumber} - ${helpCategory.replace(/_/g, ' ')}`,
        description: helpMessage,
        priority: 'medium',
        relatedOrderId: order._id,
        relatedRestaurantId: order.restaurantId._id
      });
      alert('Your support request has been submitted! Our team will respond shortly.');
      setShowHelpModal(false);
      setHelpCategory('');
      setHelpMessage('');
    } catch (error) {
      alert('Failed to submit help request: ' + (error.response?.data?.message || error.message));
    } finally {
      setHelpSubmitting(false);
    }
  };

  const orderIssueCategories = [
    { value: 'order_not_received', label: '📦 Order Not Received', desc: 'My order is taking too long or hasn\'t arrived' },
    { value: 'wrong_items', label: '❌ Wrong Items', desc: 'I received incorrect items in my order' },
    { value: 'missing_items', label: '🔍 Missing Items', desc: 'Some items are missing from my order' },
    { value: 'food_quality', label: '🍽️ Food Quality Issue', desc: 'Issues with food quality, taste, or freshness' },
    { value: 'delivery_issue', label: '🚴 Delivery Problem', desc: 'Issue with delivery partner or delivery process' },
    { value: 'payment_issue', label: '💳 Payment Issue', desc: 'Problem with payment or refund' },
    { value: 'other', label: '📝 Other Issue', desc: 'Any other issue with this order' }
  ];

  if (loading) return <div className="loading">🔄 Loading order details...</div>;
  if (!order) return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        <div className="card fade-in" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          <div style={{ fontSize: '80px', marginBottom: 'var(--space-lg)' }}>❌</div>
          <h2 style={{ color: 'var(--gray-700)' }}>Order not found</h2>
        </div>
      </div>
    </div>
  );

  const statusFlow = [
    { key: 'pending', label: 'Order Placed', icon: '📝' },
    { key: 'restaurant_confirmed', label: 'Confirmed', icon: '✅' },
    { key: 'preparing', label: 'Preparing', icon: '👨‍🍳' },
    { key: 'ready_for_pickup', label: 'Ready', icon: '📦' },
    { key: 'assigned_delivery', label: 'Assigned', icon: '🚴' },
    { key: 'picked_up', label: 'Picked Up', icon: '📦' },
    { key: 'on_the_way', label: 'On The Way', icon: '🛵' },
    { key: 'delivered', label: 'Delivered', icon: '✅' }
  ];

  const currentIndex = statusFlow.findIndex(s => s.key === order.status);

  // Check if payment is pending for online orders
  const isPaymentPending = order.paymentMethod !== 'cash' && order.paymentStatus !== 'received';

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Payment Pending Banner - Only for online orders with pending payment */}
        {isPaymentPending && (
          <div className="card fade-in" style={{
            marginBottom: 'var(--space-xl)',
            background: 'linear-gradient(135deg, rgba(255, 184, 77, 0.1) 0%, rgba(255, 167, 38, 0.1) 100%)',
            border: '2px solid rgba(255, 184, 77, 0.5)',
            textAlign: 'center',
            padding: 'var(--space-xl)'
          }}>
            <div style={{ fontSize: '60px', marginBottom: 'var(--space-md)' }}>⏳</div>
            <h2 style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: '700',
              color: 'var(--warning)',
              marginBottom: 'var(--space-sm)'
            }}>
              Waiting for Payment Confirmation
            </h2>
            <p style={{
              fontSize: 'var(--font-size-base)',
              color: 'var(--gray-700)',
              marginBottom: 'var(--space-md)'
            }}>
              Your order is created but awaiting payment confirmation. The restaurant will receive your order once payment is confirmed.
            </p>
            <p style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--gray-600)',
              fontStyle: 'italic'
            }}>
              💳 Payment Method: {order.paymentMethod.toUpperCase()}
            </p>
          </div>
        )}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            📍 Track Your Order
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Order #{order.orderNumber}
          </p>
        </div>

        {/* Live Tracking Map - Only show for active orders */}
        {order.status !== 'delivered' && order.status !== 'cancelled' ? (
          <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)', padding: 0, overflow: 'hidden', position: 'relative' }}>
            {(() => {
              // Debug logging
              console.log('📍 Order Restaurant Data:', {
                restaurantId: order.restaurantId?._id,
                restaurantName: order.restaurantId?.name,
                fullAddress: order.restaurantId?.address,
                addressCoordinates: order.restaurantId?.address?.coordinates,
                locationCoordinates: order.restaurantId?.location?.coordinates,
                fullRestaurant: order.restaurantId
              });
              return null;
            })()}
            <LiveTracking
              deliveryLocation={deliveryLocation || order.deliveryPartnerLocation}
              destinationLocation={order.deliveryAddress?.coordinates}
              restaurantLocation={
                order.restaurantId?.address?.coordinates ||
                order.restaurantId?.location?.coordinates
              }
              orderStatus={order.status}
            />
            {/* Real-time connection indicator */}
            <div style={{
              position: 'absolute',
              top: 'var(--space-sm)',
              right: 'var(--space-sm)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-xs)',
              padding: 'var(--space-xs) var(--space-sm)',
              background: socketConnected ? 'rgba(16, 185, 129, 0.9)' : 'rgba(239, 68, 68, 0.9)',
              borderRadius: 'var(--radius-full)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: '600',
              color: 'white',
              zIndex: 10
            }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'white',
                animation: socketConnected ? 'pulse 2s infinite' : 'none'
              }} />
              {socketConnected ? 'Live Tracking' : 'Reconnecting...'}
            </div>
          </div>
        ) : order.status === 'delivered' ? (
          <div className="card fade-in" style={{
            marginBottom: 'var(--space-xl)',
            textAlign: 'center',
            padding: 'var(--space-2xl)',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
            border: '2px solid rgba(16, 185, 129, 0.3)'
          }}>
            <div style={{ fontSize: '80px', marginBottom: 'var(--space-lg)' }}>🎉</div>
            <h2 style={{
              fontSize: 'var(--font-size-3xl)',
              fontWeight: '800',
              color: 'var(--success)',
              marginBottom: 'var(--space-md)'
            }}>
              Order Delivered Successfully!
            </h2>
            <p style={{
              fontSize: 'var(--font-size-lg)',
              color: 'var(--gray-700)',
              marginBottom: 'var(--space-lg)'
            }}>
              Your order has been delivered. Enjoy your meal! 🍽️
            </p>
            {!showReview && !hasReview && (
              <button
                onClick={() => setShowReview(true)}
                className="btn btn-primary btn-lg"
                style={{ marginTop: 'var(--space-md)' }}
              >
                ⭐ Rate Your Experience
              </button>
            )}
            {hasReview && (
              <div style={{
                marginTop: 'var(--space-md)',
                padding: 'var(--space-md)',
                background: 'rgba(79, 70, 229, 0.1)',
                borderRadius: 'var(--radius-md)',
                border: '2px solid rgba(79, 70, 229, 0.2)'
              }}>
                <p style={{
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--primary)',
                  fontWeight: '600',
                  margin: 0
                }}>
                  ✅ Thank you for your review!
                </p>
              </div>
            )}
          </div>
        ) : null}

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-xl)' }}>
          <div>
            {/* Order Status Timeline */}
            <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-xl)',
                color: 'var(--gray-900)'
              }}>
                🚚 Order Status
              </h2>

              <div style={{ position: 'relative' }}>
                {statusFlow.map((status, index) => {
                  const isCompleted = index <= currentIndex;
                  const isCurrent = index === currentIndex;

                  return (
                    <div key={status.key} style={{ position: 'relative', marginBottom: 'var(--space-xl)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-lg)' }}>
                        {/* Status Icon */}
                        <div style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          background: isCompleted ? 'var(--gradient-success)' : 'var(--gray-300)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '28px',
                          boxShadow: isCurrent ? 'var(--shadow-lg)' : 'var(--shadow-sm)',
                          transition: 'all var(--transition-base)',
                          position: 'relative',
                          zIndex: 2,
                          animation: isCurrent ? 'pulse 2s infinite' : 'none'
                        }}>
                          {status.icon}
                        </div>

                        {/* Status Label */}
                        <div style={{ flex: 1 }}>
                          <h3 style={{
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: '700',
                            color: isCompleted ? 'var(--gray-900)' : 'var(--gray-500)',
                            marginBottom: 'var(--space-xs)'
                          }}>
                            {status.label}
                          </h3>
                          {isCurrent && (
                            <span style={{
                              display: 'inline-block',
                              padding: 'var(--space-xs) var(--space-md)',
                              background: 'rgba(81, 207, 102, 0.1)',
                              color: 'var(--success)',
                              borderRadius: 'var(--radius-full)',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: '700'
                            }}>
                              CURRENT STATUS
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Connector Line */}
                      {index < statusFlow.length - 1 && (
                        <div style={{
                          position: 'absolute',
                          left: '29px',
                          top: '60px',
                          width: '2px',
                          height: 'calc(100% + var(--space-xl))',
                          background: isCompleted ? 'var(--success)' : 'var(--gray-300)',
                          zIndex: 1
                        }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Order Items */}
            <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-lg)',
                color: 'var(--gray-900)'
              }}>
                📦 Order Items
              </h2>
              {order.items.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 'var(--space-md)',
                  marginBottom: 'var(--space-sm)',
                  background: 'var(--gray-100)',
                  borderRadius: 'var(--radius-md)'
                }}>
                  <div>
                    <h4 style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: '700',
                      color: 'var(--gray-900)',
                      marginBottom: 'var(--space-xs)'
                    }}>
                      {item.name}
                    </h4>
                    <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)' }}>
                      Quantity: {item.quantity}
                    </p>
                  </div>
                  <p style={{
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: '800',
                    color: 'var(--primary)',
                    margin: 0
                  }}>
                    ₹{item.subtotal}
                  </p>
                </div>
              ))}
            </div>

            {/* Review Form */}
            {order.status === 'delivered' && !hasReview && (
              <div className="card fade-in">
                {!showReview ? (
                  <div style={{ textAlign: 'center', padding: 'var(--space-lg)' }}>
                    <div style={{ fontSize: '48px', marginBottom: 'var(--space-md)' }}>⭐</div>
                    <h3 style={{ marginBottom: 'var(--space-md)', color: 'var(--gray-900)' }}>
                      How was your experience?
                    </h3>
                    <button onClick={() => setShowReview(true)} className="btn btn-primary btn-lg">
                      ✍️ Leave a Review
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submitReview}>
                    <h3 style={{
                      fontSize: 'var(--font-size-2xl)',
                      fontWeight: '700',
                      marginBottom: 'var(--space-lg)',
                      color: 'var(--gray-900)'
                    }}>
                      ⭐ Leave a Review
                    </h3>

                    <div className="form-group">
                      <label>🏪 Restaurant Rating</label>
                      <select
                        value={review.restaurantRating}
                        onChange={(e) => setReview({ ...review, restaurantRating: parseInt(e.target.value) })}
                      >
                        <option value="5">⭐⭐⭐⭐⭐ Excellent</option>
                        <option value="4">⭐⭐⭐⭐ Good</option>
                        <option value="3">⭐⭐⭐ Average</option>
                        <option value="2">⭐⭐ Below Average</option>
                        <option value="1">⭐ Poor</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>💬 Restaurant Review</label>
                      <textarea
                        value={review.restaurantReview}
                        onChange={(e) => setReview({ ...review, restaurantReview: e.target.value })}
                        placeholder="Tell us about the food quality..."
                      />
                    </div>

                    <div className="form-group">
                      <label>🚴 Delivery Rating</label>
                      <select
                        value={review.deliveryRating}
                        onChange={(e) => setReview({ ...review, deliveryRating: parseInt(e.target.value) })}
                      >
                        <option value="5">⭐⭐⭐⭐⭐ Excellent</option>
                        <option value="4">⭐⭐⭐⭐ Good</option>
                        <option value="3">⭐⭐⭐ Average</option>
                        <option value="2">⭐⭐ Below Average</option>
                        <option value="1">⭐ Poor</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>💬 Delivery Review</label>
                      <textarea
                        value={review.deliveryReview}
                        onChange={(e) => setReview({ ...review, deliveryReview: e.target.value })}
                        placeholder="Tell us about the delivery experience..."
                      />
                    </div>

                    <button type="submit" className="btn btn-success btn-lg" style={{ width: '100%' }}>
                      📝 Submit Review
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - Order Summary */}
          <div style={{ position: 'sticky', top: '100px', height: 'fit-content' }}>
            <div className="card fade-in" style={{
              background: 'var(--gradient-primary)',
              color: 'white',
              border: 'none'
            }}>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-lg)',
                color: 'white'
              }}>
                🏪 {order.restaurantId.name}
              </h2>

              <div style={{
                padding: 'var(--space-md) 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                marginBottom: 'var(--space-md)'
              }}>
                <p style={{ margin: '8px 0', fontSize: 'var(--font-size-sm)', opacity: 0.9 }}>
                  <strong>Status:</strong> {order.status.replace(/_/g, ' ').toUpperCase()}
                </p>
                <p style={{ margin: '8px 0', fontSize: 'var(--font-size-sm)', opacity: 0.9 }}>
                  <strong>Payment:</strong> {order.paymentMethod.toUpperCase()}
                  {order.paymentMethod !== 'cash' && (
                    <span style={{
                      marginLeft: '8px',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '700',
                      background: order.paymentStatus === 'received' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 184, 77, 0.3)',
                      color: order.paymentStatus === 'received' ? '#10b981' : '#ffb84d'
                    }}>
                      {order.paymentStatus === 'received' ? '✅ PAID' : '⏳ PENDING'}
                    </span>
                  )}
                </p>
              </div>

              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <h3 style={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: '700',
                  marginBottom: 'var(--space-md)',
                  color: 'white'
                }}>
                  📍 Delivery Address
                </h3>
                <p style={{ margin: '4px 0', fontSize: 'var(--font-size-sm)', opacity: 0.9 }}>
                  {order.deliveryAddress.street}
                </p>
                <p style={{ margin: '4px 0', fontSize: 'var(--font-size-sm)', opacity: 0.9 }}>
                  {order.deliveryAddress.city}, {order.deliveryAddress.state}
                </p>
                <p style={{ margin: '4px 0', fontSize: 'var(--font-size-sm)', opacity: 0.9 }}>
                  {order.deliveryAddress.zipCode}
                </p>
              </div>

              <div style={{
                padding: 'var(--space-md) 0',
                borderTop: '2px solid rgba(255, 255, 255, 0.3)',
                marginTop: 'var(--space-md)'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 'var(--space-sm)',
                  fontSize: 'var(--font-size-base)',
                  opacity: 0.9
                }}>
                  <span>Subtotal</span>
                  <span>₹{order.pricing.subtotal}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 'var(--space-sm)',
                  fontSize: 'var(--font-size-base)',
                  opacity: 0.9
                }}>
                  <span>Delivery Fee</span>
                  <span>₹{order.pricing.deliveryFee}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 'var(--space-md)',
                  fontSize: 'var(--font-size-base)',
                  opacity: 0.9
                }}>
                  <span>Tax</span>
                  <span>₹{order.pricing?.totalGst ?? order.pricing.tax}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: 'var(--font-size-2xl)',
                  fontWeight: '800',
                  paddingTop: 'var(--space-md)',
                  borderTop: '2px solid rgba(255, 255, 255, 0.3)'
                }}>
                  <span>Total</span>
                  <span>₹{order.pricing.total}</span>
                </div>
              </div>
            </div>

            {/* Help & Support Button */}
            <button
              onClick={() => setShowHelpModal(true)}
              style={{
                width: '100%',
                marginTop: 'var(--space-lg)',
                padding: 'var(--space-lg)',
                background: 'white',
                border: '2px solid var(--gray-300)',
                borderRadius: 'var(--radius-lg)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-sm)',
                fontSize: 'var(--font-size-lg)',
                fontWeight: '700',
                color: 'var(--gray-700)',
                transition: 'all var(--transition-base)',
                boxShadow: 'var(--shadow-sm)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = 'var(--primary)';
                e.currentTarget.style.color = 'var(--primary)';
                e.currentTarget.style.boxShadow = 'var(--shadow-md)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'var(--gray-300)';
                e.currentTarget.style.color = 'var(--gray-700)';
                e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
              }}
            >
              🆘 Need Help with this Order?
            </button>
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {showHelpModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 'var(--space-lg)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius-xl)',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: 'var(--shadow-2xl)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: 'var(--space-xl)',
              borderBottom: '1px solid var(--gray-200)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{
                  fontSize: 'var(--font-size-2xl)',
                  fontWeight: '800',
                  color: 'var(--gray-900)',
                  margin: 0
                }}>
                  🆘 Help & Support
                </h2>
                <p style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--gray-600)',
                  margin: '4px 0 0 0'
                }}>
                  Order #{order.orderNumber}
                </p>
              </div>
              <button
                onClick={() => setShowHelpModal(false)}
                style={{
                  background: 'var(--gray-100)',
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={submitHelpRequest} style={{ padding: 'var(--space-xl)' }}>
              {/* Issue Category Selection */}
              <div style={{ marginBottom: 'var(--space-xl)' }}>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: '700',
                  color: 'var(--gray-800)',
                  marginBottom: 'var(--space-md)'
                }}>
                  What's the issue?
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                  {orderIssueCategories.map((cat) => (
                    <label
                      key={cat.value}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 'var(--space-md)',
                        padding: 'var(--space-md)',
                        background: helpCategory === cat.value ? 'rgba(79, 70, 229, 0.1)' : 'var(--gray-50)',
                        border: `2px solid ${helpCategory === cat.value ? 'var(--primary)' : 'var(--gray-200)'}`,
                        borderRadius: 'var(--radius-md)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-fast)'
                      }}
                    >
                      <input
                        type="radio"
                        name="helpCategory"
                        value={cat.value}
                        checked={helpCategory === cat.value}
                        onChange={(e) => setHelpCategory(e.target.value)}
                        style={{ marginTop: '3px' }}
                      />
                      <div>
                        <div style={{
                          fontWeight: '600',
                          color: 'var(--gray-900)',
                          fontSize: 'var(--font-size-base)'
                        }}>
                          {cat.label}
                        </div>
                        <div style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--gray-600)',
                          marginTop: '2px'
                        }}>
                          {cat.desc}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: 'var(--space-xl)' }}>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: '700',
                  color: 'var(--gray-800)',
                  marginBottom: 'var(--space-sm)'
                }}>
                  Describe your issue
                </label>
                <textarea
                  value={helpMessage}
                  onChange={(e) => setHelpMessage(e.target.value)}
                  placeholder="Please provide details about your issue so we can help you better..."
                  required
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: 'var(--space-md)',
                    border: '2px solid var(--gray-200)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-base)',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Order Info */}
              <div style={{
                background: 'var(--gray-50)',
                padding: 'var(--space-md)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-xl)'
              }}>
                <p style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--gray-600)',
                  margin: 0
                }}>
                  <strong>Restaurant:</strong> {order.restaurantId.name}<br />
                  <strong>Order Status:</strong> {order.status.replace(/_/g, ' ').toUpperCase()}<br />
                  <strong>Order Total:</strong> ₹{order.pricing.total}
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={helpSubmitting || !helpCategory}
                style={{
                  width: '100%',
                  padding: 'var(--space-lg)',
                  background: helpSubmitting || !helpCategory ? 'var(--gray-300)' : 'var(--gradient-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-lg)',
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: '700',
                  cursor: helpSubmitting || !helpCategory ? 'not-allowed' : 'pointer',
                  transition: 'all var(--transition-base)'
                }}
              >
                {helpSubmitting ? '⏳ Submitting...' : '📨 Submit Help Request'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderTracking;
